import { useCallback, useEffect, useState } from 'react';
import { marketingApi, type MarketingAbTest, type MarketingCampaign } from '@/api/marketing.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
const AB_TEST_TYPES = ['SUBJECT', 'CONTENT', 'LANDING_PAGE', 'CTA'] as const;

type Props = {
  campaign: MarketingCampaign | null;
};

export function CampaignAbTestsPanel({ campaign }: Props) {
  const [tests, setTests] = useState<MarketingAbTest[]>([]);
  const [loading, setLoading] = useState(false);
  const [testType, setTestType] = useState<string>('SUBJECT');
  const [variantA, setVariantA] = useState('');
  const [variantB, setVariantB] = useState('');

  const load = useCallback(async () => {
    if (!campaign) {
      setTests([]);
      return;
    }
    setLoading(true);
    try {
      setTests(await marketingApi.listAbTests(campaign.id));
    } catch (err) {
      showApiError(err, 'Failed to load A/B tests');
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    if (!campaign) return;
    try {
      await marketingApi.createAbTest(campaign.id, {
        testType,
        variantARef: variantA.trim() || 'variant_a',
        variantBRef: variantB.trim() || 'variant_b',
      });
      notify.success('A/B test started');
      setVariantA('');
      setVariantB('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create A/B test');
    }
  };

  const pickWinner = async (test: MarketingAbTest) => {
    try {
      const updated = await marketingApi.pickAbWinner(test.id);
      notify.success(`Winner: variant ${updated.winnerVariant}`);
      load();
    } catch (err) {
      showApiError(err, 'Failed to pick winner');
    }
  };

  if (!campaign) {
    return (
      <div className="card p-4">
        <p className="text-sm text-slate-500">Select a campaign to manage A/B tests.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-4">
      <h3 className="text-sm font-semibold text-slate-800">A/B tests</h3>
      <p className="text-xs text-slate-500">
        Winner is chosen from open/click engagement per variant ref (requires MARKETING_AB_TEST).
      </p>

      <div className="grid gap-2 sm:grid-cols-3">
        <Select
          options={AB_TEST_TYPES.map((t) => ({ value: t, label: t }))}
          value={testType}
          onChange={setTestType}
        />
        <Input
          placeholder="Variant A ref"
          value={variantA}
          onChange={(e) => setVariantA(e.target.value)}
        />
        <Input
          placeholder="Variant B ref"
          value={variantB}
          onChange={(e) => setVariantB(e.target.value)}
        />
      </div>
      <Button className="text-sm" onClick={() => void create()}>
        Start test
      </Button>

      {loading && <p className="text-xs text-slate-500">Loading tests…</p>}
      <ul className="divide-y divide-slate-100 text-sm">
        {tests.map((t) => (
          <li key={t.id} className="py-2 flex flex-wrap justify-between gap-2">
            <div>
              <p className="font-medium text-slate-700">
                {t.testType} · {t.testStatus}
              </p>
              <p className="text-xs text-slate-500">
                A: {t.variantARef ?? '—'} · B: {t.variantBRef ?? '—'}
              </p>
              {t.winnerVariant && (
                <p className="text-xs text-emerald-700 dark:text-emerald-300">Winner: {t.winnerVariant}</p>
              )}
            </div>
            {t.testStatus === 'RUNNING' && (
              <Button variant="secondary" className="text-xs" onClick={() => void pickWinner(t)}>
                Pick winner
              </Button>
            )}
          </li>
        ))}
        {!loading && tests.length === 0 && (
          <li className="text-xs text-slate-400 py-2">No tests for this campaign.</li>
        )}
      </ul>
    </div>
  );
}
